import { DataSource, SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatSort, SortDirection } from '@angular/material/sort';
import { CoreTableFilter } from '@dice/app/libraries/core-table-virtual-scrolling/filter/filter';
import { HashMap } from '@dice/app/types/hash-map';
import { DisplayValueFn } from '@dice/app/types/table.types';
import { isEmpty as _isEmpty } from 'lodash';
import { BehaviorSubject, combineLatest, interval, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, map, shareReplay, startWith, takeUntil, tap } from 'rxjs/operators';

type FilterFn<T> = (value?: T) => boolean;

/**
 * `DataSource` to be used with `CoreTable` and `MatTable`.
 *
 * Supports advanced filtering, sorting, pagination and virtual scroll.
 */
export default class CoreTableDataSource<T> extends DataSource<T> {
  /**
   * A stream of the filtered arrays of data
   */
  private readonly _filteredData = new BehaviorSubject<T[]>([]);

  private _filterMap: BehaviorSubject<HashMap<CoreTableFilter<T>>> = new BehaviorSubject<HashMap<CoreTableFilter<T>>>(
    {},
  );

  private readonly selection = new SelectionModel<T>(true, []);

  private _visibleData: BehaviorSubject<T[]> = new BehaviorSubject([]);

  private _visibleDataSubscription: Subscription = Subscription.EMPTY;

  private _data = new BehaviorSubject<T[]>([]);

  private _incrementalDataLoadSubscription: Subscription = Subscription.EMPTY;

  private readonly filteringOperationPerformed: Subject<void> = new Subject();

  private _sort: MatSort;

  private _viewport: CdkVirtualScrollViewport;

  get filteredData(): BehaviorSubject<T[]> {
    return this._filteredData;
  }

  get sort(): MatSort {
    return this._sort;
  }

  set sort(matSort: MatSort) {
    this._sort = matSort;
    this.updateChangeSubscriptions();
  }

  get viewport(): CdkVirtualScrollViewport {
    return this._viewport;
  }

  set viewport(viewport: CdkVirtualScrollViewport) {
    this._viewport = viewport;
    this.updateChangeSubscriptions();
  }

  /**
   * The full array of data
   */
  get data(): T[] {
    return this._data.getValue();
  }

  set data(data: T[]) {
    if (!!data && data.length > 0) {
      this._data.next(data);
    }
  }

  /**
   * The array of currently applied `CoreTableFilter`.
   */
  get filters(): CoreTableFilter<T>[] {
    return Object.keys(this.filterMap).map((key) => this.filterMap[key]);
  }

  get filterMap(): HashMap<CoreTableFilter<T>> {
    return this._filterMap.getValue();
  }

  /**
   * The array of currently selected items.
   */
  get selected(): T[] {
    return this.selection.selected;
  }

  /**
   * Whether all visible items are selected.
   */
  get selectedAll(): boolean {
    return this.selection.hasValue() && this.selected.length === this._filteredData.getValue().length;
  }

  /**
   * A stream of changes made on selections.
   */
  get selectionChanged(): Observable<SelectionChange<T>> {
    return this.selection.changed;
  }

  constructor(initialData: T[]) {
    super();
    this._data.next(initialData);
    this.updateChangeSubscriptions();
  }

  private updateChangeSubscriptions(): void {
    const dataStream = this._data;

    const filteredData = combineLatest([dataStream, this._filterMap]).pipe(
      map(([data]) => {
        return this.performCombinedFiltering(data);
      }),
    );

    const sortChange = this._sort
      ? merge(this._sort.sortChange, this._sort.initialized).pipe(debounceTime(500))
      : of(null);

    const orderedData = combineLatest([filteredData, sortChange]).pipe(
      map(([data]) => {
        if (this._sort) {
          return this.sortData(data, this._sort);
        }
        return data;
      }),
      tap((data: T[]): void => {
        this._filteredData.next(data);
      }),
    );

    const viewportChange = !this._viewport
      ? of({ start: null, end: null })
      : this._viewport.renderedRangeStream.pipe(startWith({ start: null, end: null }));

    const sliced = combineLatest([orderedData, viewportChange]).pipe(
      map(([data, { start, end }]) => {
        if (start == null || end == null) {
          return data;
        }
        return data.slice(start, end);
      }),
    );

    this._visibleDataSubscription.unsubscribe();
    this._visibleDataSubscription = sliced.subscribe((data) => {
      if (this._visibleData.getValue().length > 0) {
        this._incrementalDataLoadSubscription.unsubscribe();
        this._visibleData.next(data);
      } else {
        this.incrementallyLoadData(data);
      }
    });
  }

  private incrementallyLoadData(data: T[]): void {
    const MILLI_SECONDS = 1;
    const MAX_AMOUNT = data.length;
    const LOAD_AMOUNT = 15;
    let start = 0;
    let end = LOAD_AMOUNT;
    const unsubscriber: Subject<void> = new Subject();

    this._incrementalDataLoadSubscription.unsubscribe();
    this._visibleData.next([]);
    this._incrementalDataLoadSubscription = interval(MILLI_SECONDS)
      .pipe(
        map(() => data.slice(start, end)),
        takeUntil(unsubscriber),
        takeUntil(this.filteringOperationPerformed),
        // startWith([]),
        shareReplay(),
      )
      .subscribe((next: T[]) => {
        start = end;
        if (next.length === 0 || end === MAX_AMOUNT) {
          end = -1;
        } else {
          end = end + LOAD_AMOUNT < MAX_AMOUNT ? end + LOAD_AMOUNT : MAX_AMOUNT;
        }
        this._visibleData.next([
          ...this._visibleData.getValue(),
          ...next,
          // ...next.filter((t: T) => filterOneItem(this.filterMap, t)),
        ]);
        if (end === -1) {
          unsubscriber.next();
        }
      });
  }

  /**
   * Dummy function that will be replaced by the SortTableDirective.
   * @param data
   * @param properties
   */
  sortData(data: T[], properties: { direction: SortDirection; active: DisplayValueFn<T> | string }): T[] {
    return data;
  }

  /**
   * Clears all filters set with `setFilter`.
   */
  clearFilters(): void {
    this._filterMap.next({});
  }

  /**
   * Clears all of the selected items.
   */
  clearSelection(): void {
    this.selection.clear();
  }

  /**
   * Whether an item is selected.
   */
  isSelected(item: T): boolean {
    return this.selection.isSelected(item);
  }

  /**
   * Selects one or more items.
   */
  select(...items: T[]): void {
    this.selection.select(...items);
  }

  /**
   * Sets a new `CoreTableFilter` based on its key
   * and applies all existing filters to the `DataSource`.
   * @param key Unique key, usually representing the property path to retrieve a value from an item.
   * @param predicate How to filter the values extracted by `key`, like `Array.prototype.filter`.
   * @param valueFn Optional function to extract a value from each item.
   *
   * Default: `item => item[key]`.
   *
   * Note: Supports nested property paths, e.g. `'my.nested.prop'`
   *
   * @example
   * const dataSource = new CoreTableDataSource([1, 2, 3, 4]);
   * dataSource.setFilter({
   *   key: 'prop',
   *   predicate: value => value % 2
   * });
   * console.log(dataSource.data);
   * // [2, 4]
   *
   * dataSource.setFilter({
   *   key: 'propPlusOne',
   *   predicate: value => value % 2,
   *   valueFn: item => item + 1
   * });
   * console.log(dataSource.data);
   * // [1, 3]
   *
   * @deprecated
   */
  setFilter({ key, predicate, valueFn = (item: T): string => getProperty(item, key) }: CoreTableFilter<T>): void {
    const newKey = !this.filterMap[key];
    const newFilter = { key, predicate, valueFn };
    this.filterMap[key] = newFilter;

    if (newKey) {
      this.filter(this._filteredData.getValue(), { [key]: newFilter });
    } else {
      this.filter(this.data, this.filterMap);
    }
  }

  setFilterOnMultipleKeys(parameters: HashMap<CoreTableFilter<T>>, skipFilteringOperation: boolean): void {
    this._filterMap.next(parameters);
    // if (!skipFilteringOperation) {
    //   this.combinedFilter(this.allData);
    // }
  }

  /**
   * Toggles an item between selected and deselected.
   */
  toggle(...items: T[]): void {
    items.forEach((item: T) => {
      this.selection.toggle(item);
    });
  }

  /**
   * Toggles the selection of all visible items.
   *
   * If all are selected, deselects all.
   *
   * If some or none are selected, selects all.
   */
  toggleAll(): void {
    if (this.selectedAll) {
      this.selection.clear();
    } else {
      this.selection.select(...this._filteredData.getValue());
    }
  }

  connect(): Observable<T[]> {
    return this._visibleData;
  }

  disconnect(): void {
    this._visibleDataSubscription.unsubscribe();
    this._visibleData.complete();
    delete this._visibleData;
    this.filteringOperationPerformed.complete();
    this._incrementalDataLoadSubscription.unsubscribe();
    this._filterMap.complete();
    delete this._filterMap;
    this._data.complete();
    delete this._data;
    this.selection.clear();
    this._viewport.detach();
    this._viewport = null;
  }

  clearData(): void {
    this._data.next([]);
  }

  private performCombinedFiltering(_data: T[]): T[] {
    const data = _data && Array.isArray(_data) ? _data : [];
    const filterFunctions: FilterFn<T>[] = this.getFilterFunctions(this.filterMap);
    const filteredData = data.filter((t: T) => {
      return filterFunctions.reduce<boolean>((accumulator: boolean, filter: FilterFn<T>) => {
        return accumulator && filter(t);
      }, true);
    });
    return filteredData;
  }

  private combinedFilter(_data: T[]): void {
    this._filteredData.next(this.performCombinedFiltering(_data));
    this.filteringOperationPerformed.next();
  }

  /**
   * @deprecated
   * @param _data
   * @param filters
   */
  private filter(_data: T[], filters: HashMap<CoreTableFilter<T>>): void {
    const data = _data && Array.isArray(_data) ? _data : [];
    this._filteredData.next(data.filter((t: T) => filterOneItem(filters, t)));
  }

  private getFilterFunctions(filters: HashMap<CoreTableFilter<T>>): FilterFn<T>[] {
    return Object.keys(filters).reduce<FilterFn<T>[]>((accumulator: FilterFn<T>[], filterKey: string) => {
      const filterFunction: FilterFn<T> = !_isEmpty(filters[filterKey])
        ? filterOneItem.bind(this, { [filterKey]: filters[filterKey] })
        : (): boolean => true;
      return accumulator.concat(filterFunction);
    }, []);
  }
}

function filterOneSome<T>(filters: HashMap<CoreTableFilter<T>>, item: T): boolean {
  const values = Object.keys(filters).map((key) => filters[key]);
  return values.some(({ predicate, valueFn }) => predicate(valueFn(item)));
}

function filterOneItem<T>(filters: HashMap<CoreTableFilter<T>>, item: T): boolean {
  return Object.values(filters).every(({ predicate, valueFn }) => predicate(valueFn(item)));
}

function getProperty<T>(item: T, propertyPath: string): string {
  return propertyPath.split('.').reduce((obj, prop) => obj && obj[prop], item);
}
